import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import Button from "../Button";
import { Typography } from "../Typography";
import TextField from "../TextField";

// const getStudentName = (idea) => {
//   if (!idea) return "";
//   return (
//     idea.student_name ||
//     idea.studentName ||
//     idea.submitter?.name ||
//     idea.user?.name ||
//     idea.created_by_name ||
//     ""
//   ).trim();
// };

const AddTaskModal = ({ isOpen, onClose, onSave, ideas: ideasProp = [] }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    idea: "",
    // student: "",
    priority: "",
    dueDate: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const ideas = useMemo(
    () =>
      (ideasProp || []).map((idea) => {
        const id = idea.id != null ? String(idea.id) : "";
        const label = idea.name || idea.title || idea.idea_name || idea.startupName || "Untitled";
        return { value: id, label };
      }),
    [ideasProp]
  );

  // const uniqueStudents = useMemo(() => {
  //   const names = [...new Set((ideasProp || []).map(getStudentName).filter(Boolean))];
  //   return names.map((name) => ({ value: name, label: name }));
  // }, [ideasProp]);

  const priorities = [
    { value: "High", label: "High" },
    { value: "Medium", label: "Medium" },
    { value: "Low", label: "Low" },
  ];

  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: "",
        description: "",
        idea: "",
        priority: "",
        dueDate: "",
      });
      setErrors({});
      setSubmitError("");
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.idea) {
      newErrors.idea = "Idea is required";
    }

    // if (!formData.student) {
    //   newErrors.student = "Student is required";
    // }

    if (!formData.priority) {
      newErrors.priority = "Priority is required";
    }

    if (!formData.dueDate) {
      newErrors.dueDate = "Due date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitError("");
    setSubmitting(true);
    try {
      const apiPayload = {
        title: formData.title.trim(),
        task_details: formData.description?.trim() || "",
        idea_id: Number(formData.idea) || 0,
        priority: (formData.priority || "").toLowerCase(),
        end_date: formData.dueDate || "",
      };
      const result = await onSave?.(apiPayload);
      if (result?.success !== false) {
        onClose();
      } else {
        setSubmitError(result?.error || "Failed to create task. Please try again.");
      }
    } catch (err) {
      setSubmitError(err?.error || err?.message || "An error occurred while creating the task.");
    } finally {
      setSubmitting(false);
    }
  };

  const getDaySuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 inline-flex items-center justify-center rounded-lg bg-purple-100">
              <Icon icon="lucide:plus" className="h-5 w-5 text-purple-600" />
            </div>
            <div className="text-left">
              <Typography as="h2" className="text-xl font-bold text-primary-900">
                Add New Task
              </Typography>
              <Typography as="p" className="text-sm text-tertiary-600 mt-1">
                Please fill in the following details.
              </Typography>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <Icon icon="lucide:x" className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col gap-6">
            {/* Title Field */}
            <div className="text-left">
              <TextField
                label="Title"
                name="title"
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                placeholder="Enter task title"
                required
                error={errors.title}
                helperText={errors.title}
              />
            </div>

            {/* Description Field */}
            <div className="text-left">
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                placeholder="Add a description here"
                multiline
                rows={4}
                error={errors.description}
                helperText={errors.description}
              />
            </div>

            {/* Idea Field */}
            <div className="text-left">
              <label className="text-sm font-medium text-slate-700 block mb-1 text-left">
                Idea <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="idea"
                  value={formData.idea}
                  onChange={(e) => handleInputChange("idea", e.target.value)}
                  className={`w-full rounded-lg border ${
                    errors.idea ? "border-red-400" : "border-slate-200"
                  } bg-white pl-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none text-left`}
                >
                  <option value="">Select an idea</option>
                  {ideas.map((idea) => (
                    <option key={idea.value} value={idea.value}>
                      {idea.label}
                    </option>
                  ))}
                </select>
                <Icon
                  icon="lucide:chevron-down"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                />
              </div>
              {errors.idea && (
                <p className="text-xs text-red-500 mt-1">{errors.idea}</p>
              )}
            </div>

            {/* Student Field */}
            {/* <div className="text-left">
              <label className="text-sm font-medium text-slate-700 block mb-1 text-left">
                Student <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="student"
                  value={formData.student}
                  onChange={(e) => handleInputChange("student", e.target.value)}
                  className={`w-full rounded-lg border ${
                    errors.student ? "border-red-400" : "border-slate-200"
                  } bg-white pl-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none text-left`}
                >
                  <option value="">Select a student</option>
                  {uniqueStudents.map((student) => (
                    <option key={student.value} value={student.value}>
                      {student.label}
                    </option>
                  ))}
                </select>
                <Icon
                  icon="lucide:chevron-down"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                />
              </div>
              {errors.student && (
                <p className="text-xs text-red-500 mt-1">{errors.student}</p>
              )}
            </div> */}

            {/* Priority Field */}
            <div className="text-left">
              <label className="text-sm font-medium text-slate-700 block mb-1 text-left">
                Priority <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={(e) => handleInputChange("priority", e.target.value)}
                  className={`w-full rounded-lg border ${
                    errors.priority ? "border-red-400" : "border-slate-200"
                  } bg-white pl-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none text-left`}
                >
                  <option value="">Select priority</option>
                  {priorities.map((priority) => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
                <Icon
                  icon="lucide:chevron-down"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                />
              </div>
              {errors.priority && (
                <p className="text-xs text-red-500 mt-1">{errors.priority}</p>
              )}
            </div>

            {/* Due Date Field */}
            <div className="text-left">
              <TextField
                label="Due Date"
                name="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleInputChange("dueDate", e.target.value)}
                required
                error={errors.dueDate}
                helperText={errors.dueDate}
              />
            </div>
            {submitError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                {submitError}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {submitting ? "Adding..." : "Add Task"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddTaskModal;

